import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Layout } from '@/components/layout';
import { useAuthStore } from '@/lib/auth-store';
import { useQuery } from '@tanstack/react-query';
import { ShopifyTargetStoreSelector } from '@/components/shopify-target-store';
import {
  Send, Bot, User, Loader2, CheckCircle2, XCircle, AlertCircle,
  Sparkles, ChevronDown, ChevronUp, X, ListFilter, RotateCcw, Pencil,
} from 'lucide-react';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

// ── Types ─────────────────────────────────────────────────────────────────────

interface ShopifyStore { id: string; nickname: string; storeUrl: string }

type MessageRole = 'user' | 'assistant';

interface TextMessage {
  id: string;
  role: MessageRole;
  type: 'text';
  content: string;
}

interface PlanMessage {
  id: string;
  role: 'assistant';
  type: 'plan';
  storeId: string;
  tool: string;
  params: any;
  summary: string;
  state: 'pending' | 'running' | 'done' | 'error' | 'cancelled';
}

interface ProgressLog {
  type: string;
  [key: string]: any;
}

type Message = TextMessage | PlanMessage;

// ── Helpers ───────────────────────────────────────────────────────────────────

function uid() { return Math.random().toString(36).slice(2); }

function toolLabel(tool: string): string {
  return ({
    bulk_product_update:          'Bulk Product Update',
    sort_collection_oos:          'Sort Collection (OOS → End)',
    generate_fitment:             'Generate Vehicle Fitment',
    push_widget_to_theme:         'Push Widget to Theme',
    update_collection_metafields: 'Update Collection Metafields',
    create_collection:            'Create Collection',
  } as Record<string, string>)[tool] ?? tool;
}

// ── Chips ─────────────────────────────────────────────────────────────────────

function FilterRuleChip({ rule }: { rule: any }) {
  const opLabels: Record<string, string> = {
    equals: '=', not_equals: '≠', contains: 'contains', not_contains: "doesn't contain",
    starts_with: 'starts with', ends_with: 'ends with', has_tag: 'has tag',
    not_has_tag: 'no tag', greater_than: '>', less_than: '<', is_blank: 'is empty', is_not_blank: 'is set',
  };
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-muted text-xs text-foreground">
      <span className="text-muted-foreground">{rule.type}</span>
      <span className="text-muted-foreground">{opLabels[rule.operator] ?? rule.operator}</span>
      {rule.value && <span className="text-foreground font-medium">"{rule.value}"</span>}
    </span>
  );
}

function UpdateRuleChip({ rule }: { rule: any }) {
  const verb: Record<string, string> = {
    add_tag: '+ tag', remove_tag: '− tag', set_tags: 'set tags', set_vendor: 'set vendor',
    set_product_type: 'set type', set_status: 'set status', set_title: 'set title',
    prepend_title: 'prepend', append_title: 'append', find_replace_title: 'find/replace',
    set_price: 'set price', increase_price_pct: '+ price %', decrease_price_pct: '− price %',
    increase_price_amt: '+ price $', decrease_price_amt: '− price $',
    set_compare_at_price: 'set compare-at', clear_compare_at_price: 'clear compare-at',
  };
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-primary/10 border border-primary/20 text-xs text-primary">
      {verb[rule.type] ?? rule.type}
      {rule.value && <span className="text-foreground font-medium ml-1">"{rule.value}"</span>}
      {rule.find && <span className="text-foreground font-medium ml-1">"{rule.find}" → "{rule.replace}"</span>}
    </span>
  );
}

// ── Progress log entry ────────────────────────────────────────────────────────

function ProgressEntry({ log }: { log: ProgressLog }) {
  if (log.type === 'product') {
    return (
      <div className="flex items-start gap-2 text-xs py-0.5">
        {log.success
          ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />
          : <XCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0 mt-0.5" />}
        <span className={log.success ? 'text-foreground' : 'text-red-700 dark:text-red-300'}>
          {log.title}
          {log.fitment && log.success && (
            <span className="text-muted-foreground ml-1">
              ({[log.fitment.make, log.fitment.model, log.fitment.year].filter(Boolean).join(' · ')})
            </span>
          )}
          {log.error && <span className="text-red-400 ml-2">{log.error}</span>}
        </span>
      </div>
    );
  }
  if (log.type === 'collection_result') {
    return (
      <div className="flex items-start gap-2 text-xs py-0.5">
        {log.error
          ? <XCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0 mt-0.5" />
          : log.skipped
            ? <span className="w-3.5 h-3.5 flex-shrink-0 text-center text-muted-foreground">—</span>
            : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />}
        <span className={log.error ? 'text-red-700 dark:text-red-300' : log.skipped ? 'text-muted-foreground' : 'text-foreground'}>
          {log.title}
          {log.skipped && log.reason && <span className="text-muted-foreground ml-1 italic">({log.reason})</span>}
          {log.movedCount > 0 && <span className="text-emerald-400 ml-1">{log.movedCount} OOS moved</span>}
          {log.message && !log.error && <span className="text-muted-foreground ml-1">({log.message})</span>}
          {log.error && <span className="text-red-400 ml-2">{log.error}</span>}
        </span>
      </div>
    );
  }
  if (log.type === 'status') {
    return <p className="text-xs text-muted-foreground py-0.5 italic">{log.message}</p>;
  }
  return null;
}

// ── Collection Picker ─────────────────────────────────────────────────────────

interface CollectionEntry {
  gqlId: string;
  title: string;
  isSmart: boolean;
}

interface CollectionPickerProps {
  storeId: string;
  token: string;
  collectionHint: string;
  selectedIds: Set<string>;
  onChange: (ids: Set<string>) => void;
  disabled?: boolean;
}

function CollectionPicker({ storeId, token, collectionHint, selectedIds, onChange, disabled }: CollectionPickerProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['collections', storeId],
    queryFn: async () => {
      const r = await fetch(`${BASE}/api/stores/${storeId}/collections`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Failed to fetch collections');
      return r.json() as Promise<{ custom: { id: number; title: string }[]; smart: { id: number; title: string }[] }>;
    },
    enabled: !!storeId && !!token,
    staleTime: 2 * 60 * 1000,
  });

  const collections: CollectionEntry[] = useMemo(() => {
    if (!data) return [];
    const custom = (data.custom ?? []).map(c => ({
      gqlId: `gid://shopify/Collection/${c.id}`,
      title: c.title,
      isSmart: false,
    }));
    const smart = (data.smart ?? []).map(c => ({
      gqlId: `gid://shopify/Collection/${c.id}`,
      title: c.title,
      isSmart: true,
    }));
    return [...custom, ...smart].sort((a, b) => a.title.localeCompare(b.title));
  }, [data]);

  // Pre-select collections matching the hint when data first loads
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || collections.length === 0) return;
    seededRef.current = true;
    const hint = (collectionHint ?? '').toLowerCase().trim();
    if (!hint || hint === 'all') {
      // "all" hint → select all manual collections
      const allManual = new Set(collections.filter(c => !c.isSmart).map(c => c.gqlId));
      onChange(allManual);
    } else {
      const matched = collections.filter(c =>
        c.title.toLowerCase() === hint || c.title.toLowerCase().includes(hint)
      );
      if (matched.length > 0) onChange(new Set(matched.map(c => c.gqlId)));
    }
  }, [collections]);

  const allManualIds = useMemo(() => collections.filter(c => !c.isSmart).map(c => c.gqlId), [collections]);
  const allManualSelected = allManualIds.length > 0 && allManualIds.every(id => selectedIds.has(id));

  function toggleAll() {
    if (allManualSelected) {
      onChange(new Set());
    } else {
      onChange(new Set(allManualIds));
    }
  }

  function toggle(gqlId: string) {
    const next = new Set(selectedIds);
    if (next.has(gqlId)) next.delete(gqlId);
    else next.add(gqlId);
    onChange(next);
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-1">
        <Loader2 className="w-3 h-3 animate-spin" />
        Loading collections…
      </div>
    );
  }

  if (isError || collections.length === 0) {
    return <p className="text-xs text-muted-foreground italic py-1">Could not load collections.</p>;
  }

  return (
    <div className="space-y-1">
      {/* Select all toggle */}
      <label className="flex items-center gap-2 cursor-pointer group py-0.5">
        <input
          type="checkbox"
          checked={allManualSelected}
          onChange={toggleAll}
          disabled={disabled}
          className="w-3.5 h-3.5 accent-primary"
        />
        <span className="text-xs font-semibold text-foreground group-hover:text-foreground transition-colors">
          All manual collections
        </span>
      </label>
      <div className="border-t border-border/60 pt-1 max-h-40 overflow-y-auto custom-scrollbar space-y-0.5 pr-1">
        {collections.map(col => (
          <label
            key={col.gqlId}
            className={`flex items-center gap-2 py-0.5 cursor-pointer group ${col.isSmart ? 'opacity-50' : ''}`}
            title={col.isSmart ? 'Smart collections cannot be manually reordered' : undefined}
          >
            <input
              type="checkbox"
              checked={selectedIds.has(col.gqlId)}
              onChange={() => toggle(col.gqlId)}
              disabled={disabled || col.isSmart}
              className="w-3.5 h-3.5 accent-primary flex-shrink-0"
            />
            <span className="text-xs text-foreground group-hover:text-foreground transition-colors truncate">
              {col.title}
            </span>
            {col.isSmart && (
              <span className="text-[9px] text-slate-600 flex-shrink-0">smart</span>
            )}
          </label>
        ))}
      </div>
    </div>
  );
}

// ── Plan Card ─────────────────────────────────────────────────────────────────

interface PlanCardProps {
  msg: PlanMessage;
  storeId: string;
  token: string;
  onConfirm: (overrideParams?: any) => void;
  onCancel: () => void;
  onRerun: (overrideParams?: any) => void;
  progressLogs: ProgressLog[];
  progressStats: { processed: number; total: number; updated: number; errors: number } | null;
  resultSummary: string | null;
}

function PlanCard({ msg, storeId, token, onConfirm, onCancel, onRerun, progressLogs, progressStats, resultSummary }: PlanCardProps) {
  const [logsExpanded, setLogsExpanded] = useState(false);
  const [selectedCollectionIds, setSelectedCollectionIds] = useState<Set<string>>(new Set());
  const [editMode, setEditMode] = useState(false);
  const [editedJson, setEditedJson] = useState('');
  const [jsonParseError, setJsonParseError] = useState('');

  const filterRules: any[] = msg.params?.filterRules ?? [];
  const updateRules: any[] = msg.params?.updateRules ?? [];

  const pct = progressStats && progressStats.total > 0
    ? Math.round((progressStats.processed / progressStats.total) * 100)
    : 0;

  const isSortOos = msg.tool === 'sort_collection_oos';

  // Extra params display for non-bulk, non-sort tools
  const extraParams: Array<{ label: string; value: string }> = [];
  const fitmentFilterRules: any[] = msg.tool === 'generate_fitment' ? (msg.params?.filterRules ?? []) : [];
  if (msg.tool === 'generate_fitment') {
    if (msg.params?.modelSelection) extraParams.push({ label: 'Mode', value: msg.params.modelSelection === 'title-parse' ? 'Title Parse (fast)' : 'AI Analysis' });
    if (fitmentFilterRules.length === 0 && msg.params?.filterHint) extraParams.push({ label: 'Target', value: msg.params.filterHint });
    if (msg.params?.namespace) extraParams.push({ label: 'Namespace', value: msg.params.namespace });
  }
  if (msg.tool === 'update_collection_metafields') {
    if (msg.params?.collectionHint) extraParams.push({ label: 'Collection', value: msg.params.collectionHint });
    if (msg.params?.key) extraParams.push({ label: 'Key', value: `${msg.params.namespace}.${msg.params.key}` });
    if (msg.params?.value) extraParams.push({ label: 'Value', value: msg.params.value });
  }
  if (msg.tool === 'create_collection') {
    if (msg.params?.title) extraParams.push({ label: 'Title', value: msg.params.title });
    if (msg.params?.collectionType) extraParams.push({ label: 'Type', value: msg.params.collectionType });
    if (msg.params?.description) extraParams.push({ label: 'Description', value: msg.params.description });
  }
  if (msg.tool === 'push_widget_to_theme' && msg.params?.collectionHandle) {
    extraParams.push({ label: 'Collection', value: msg.params.collectionHandle });
  }

  function handleConfirmClick() {
    if (isSortOos) {
      const ids = [...selectedCollectionIds];
      onConfirm({ ...msg.params, selectedCollectionIds: ids.length > 0 ? ids : "all" });
    } else {
      onConfirm();
    }
  }

  function openEditMode() {
    setEditedJson(JSON.stringify(msg.params, null, 2));
    setJsonParseError('');
    setEditMode(true);
  }

  function handleRunEdited() {
    try {
      const parsed = JSON.parse(editedJson);
      setJsonParseError('');
      setEditMode(false);
      onRerun(parsed);
    } catch {
      setJsonParseError('Invalid JSON — please fix the syntax before running.');
    }
  }

  return (
    <div className="rounded-2xl border border-border bg-muted/50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border/60 bg-primary/5">
        <Sparkles className="w-4 h-4 text-primary flex-shrink-0" />
        <span className="text-xs font-semibold text-primary uppercase tracking-wider">{toolLabel(msg.tool)}</span>
      </div>

      {/* Summary */}
      <div className="px-4 py-3">
        <p className="text-sm text-foreground leading-relaxed">{msg.summary}</p>
      </div>

      {/* Filter rules — bulk_product_update */}
      {filterRules.length > 0 && (
        <div className="px-4 pb-3">
          <p className="text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">Filters</p>
          <div className="flex flex-wrap gap-1.5">
            {filterRules.map((r: any, i: number) => <FilterRuleChip key={i} rule={r} />)}
          </div>
        </div>
      )}
      {filterRules.length === 0 && msg.tool === 'bulk_product_update' && (
        <div className="px-4 pb-3">
          <p className="text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">Filters</p>
          <span className="text-xs text-muted-foreground italic">All products (no filter)</span>
        </div>
      )}

      {/* Filter rules — generate_fitment */}
      {fitmentFilterRules.length > 0 && (
        <div className="px-4 pb-3">
          <p className="text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">Product Filter</p>
          <div className="flex flex-wrap gap-1.5">
            {fitmentFilterRules.map((r: any, i: number) => <FilterRuleChip key={i} rule={r} />)}
          </div>
        </div>
      )}
      {fitmentFilterRules.length === 0 && msg.tool === 'generate_fitment' && (
        <div className="px-4 pb-3">
          <p className="text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">Product Filter</p>
          <span className="text-xs text-muted-foreground italic">All products without fitment data</span>
        </div>
      )}

      {/* Update rules */}
      {updateRules.length > 0 && (
        <div className="px-4 pb-3">
          <p className="text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">Operations</p>
          <div className="flex flex-wrap gap-1.5">
            {updateRules.map((r: any, i: number) => <UpdateRuleChip key={i} rule={r} />)}
          </div>
        </div>
      )}

      {/* Collection picker for sort_collection_oos */}
      {isSortOos && (
        <div className="px-4 pb-3">
          <p className="text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <ListFilter className="w-3 h-3" />
            Choose collections
          </p>
          {msg.state === 'pending' ? (
            <CollectionPicker
              storeId={storeId}
              token={token}
              collectionHint={msg.params?.collectionHint ?? ''}
              selectedIds={selectedCollectionIds}
              onChange={setSelectedCollectionIds}
            />
          ) : (
            <p className="text-xs text-muted-foreground">
              {selectedCollectionIds.size > 0
                ? `${selectedCollectionIds.size} collection(s) selected`
                : 'All collections'}
            </p>
          )}
        </div>
      )}

      {/* Extra params for other tools */}
      {extraParams.length > 0 && (
        <div className="px-4 pb-3">
          <div className="flex flex-wrap gap-3">
            {extraParams.map((p, i) => (
              <div key={i} className="text-xs">
                <span className="text-muted-foreground">{p.label}: </span>
                <span className="text-foreground font-medium">{p.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Progress bar */}
      {msg.state === 'running' && progressStats && progressStats.total > 0 && (
        <div className="px-4 pb-3">
          <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
            <span>{progressStats.processed} / {progressStats.total}</span>
            <span>{pct}%</span>
          </div>
          <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${pct}%` }} />
          </div>
          <div className="flex gap-3 mt-1.5 text-[10px]">
            <span className="text-emerald-400">✓ {progressStats.updated} updated</span>
            {progressStats.errors > 0 && <span className="text-red-400">✗ {progressStats.errors} errors</span>}
          </div>
        </div>
      )}
      {msg.state === 'running' && !progressStats && (
        <div className="px-4 pb-3">
          <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary/50 rounded-full animate-pulse" style={{ width: '60%' }} />
          </div>
        </div>
      )}

      {/* Progress logs */}
      {progressLogs.length > 0 && (
        <div className="px-4 pb-3">
          <button
            onClick={() => setLogsExpanded(v => !v)}
            className="flex items-center gap-1 text-[10px] font-bold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors mb-1"
          >
            {logsExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {progressLogs.length} log entries
          </button>
          {logsExpanded && (
            <div className="space-y-0.5 max-h-48 overflow-y-auto custom-scrollbar pr-1">
              {progressLogs.map((log, i) => <ProgressEntry key={i} log={log} />)}
            </div>
          )}
        </div>
      )}

      {/* Result */}
      {msg.state === 'done' && resultSummary && (
        <div className="px-4 pb-3">
          <div className="flex items-center gap-2 py-2 px-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
            <p className="text-xs text-emerald-700 dark:text-emerald-300">{resultSummary}</p>
          </div>
        </div>
      )}
      {msg.state === 'error' && resultSummary && (
        <div className="px-4 pb-3">
          <div className="flex items-center gap-2 py-2 px-3 rounded-lg bg-red-500/10 border border-red-500/20">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <p className="text-xs text-red-700 dark:text-red-300">{resultSummary}</p>
          </div>
        </div>
      )}
      {msg.state === 'cancelled' && (
        <div className="px-4 pb-3">
          <p className="text-xs text-muted-foreground italic">Cancelled.</p>
        </div>
      )}

      {/* Actions */}
      {msg.state === 'pending' && (
        <div className="px-4 py-3 border-t border-border/60 flex items-center gap-2">
          <button
            onClick={handleConfirmClick}
            disabled={isSortOos && selectedCollectionIds.size === 0}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-black text-xs font-semibold hover:bg-primary/80 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            Confirm & Run
          </button>
          <button
            onClick={onCancel}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/50 text-xs transition-colors"
          >
            <X className="w-3.5 h-3.5" />
            Cancel
          </button>
        </div>
      )}
      {msg.state === 'running' && (
        <div className="px-4 py-3 border-t border-border/60 flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Running…
        </div>
      )}

      {/* Re-run / Edit & Re-run — shown after the plan has finished */}
      {(msg.state === 'done' || msg.state === 'error' || msg.state === 'cancelled') && !editMode && (
        <div className="px-4 py-3 border-t border-border/60 flex items-center gap-2">
          <button
            onClick={() => onRerun()}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/50 text-xs transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Re-run
          </button>
          <button
            onClick={openEditMode}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/50 text-xs transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" />
            Edit &amp; Re-run
          </button>
        </div>
      )}

      {/* Inline JSON editor */}
      {editMode && (
        <div className="border-t border-border/60">
          <div className="px-4 pt-3 pb-2">
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Pencil className="w-3 h-3" />
              Edit plan parameters
            </p>
            <textarea
              value={editedJson}
              onChange={e => { setEditedJson(e.target.value); setJsonParseError(''); }}
              spellCheck={false}
              rows={12}
              className="w-full rounded-xl bg-muted border border-border focus:border-primary/50 text-xs text-foreground font-mono px-3 py-2.5 resize-y focus:outline-none custom-scrollbar"
            />
            {jsonParseError && (
              <p className="mt-1.5 text-[10px] text-red-400 flex items-center gap-1">
                <AlertCircle className="w-3 h-3 flex-shrink-0" />
                {jsonParseError}
              </p>
            )}
          </div>
          <div className="px-4 pb-3 flex items-center gap-2">
            <button
              onClick={handleRunEdited}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-black text-xs font-semibold hover:bg-primary/80 transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Run with edits
            </button>
            <button
              onClick={() => { setEditMode(false); setJsonParseError(''); }}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/50 text-xs transition-colors"
            >
              <X className="w-3.5 h-3.5" />
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Suggested prompts ─────────────────────────────────────────────────────────

const SUGGESTED_PROMPTS = [
  "Add the tag 'sale' to all products by vendor Toyota",
  "Remove the tag 'clearance' from all products",
  "Set all DRAFT products to ACTIVE status",
  "Increase prices by 10% for all Lift Kits products",
  "Generate vehicle fitment for all uncatalogued products",
  "Sort the Wheels collection so out-of-stock products appear at the end",
];

// ── Main Page ─────────────────────────────────────────────────────────────────

export function Copilot() {
  const { token } = useAuthStore();
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [progressLogs, setProgressLogs] = useState<Record<string, ProgressLog[]>>({});
  const [progressStats, setProgressStats] = useState<Record<string, any>>({});
  const [resultSummaries, setResultSummaries] = useState<Record<string, string>>({});
  const [storeChangedNotice, setStoreChangedNotice] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const prevStoreIdRef = useRef<string>('');

  // Fetch stores
  const { data: storesData } = useQuery({
    queryKey: ['stores'],
    queryFn: async () => {
      const r = await fetch(`${BASE}/api/stores`, { headers: { Authorization: `Bearer ${token}` } });
      return r.json();
    },
    enabled: !!token,
  });
  const stores: ShopifyStore[] = Array.isArray(storesData) ? storesData : (storesData?.stores ?? []);

  // For single-store accounts: resolve the active store immediately without
  // waiting for the auto-select useEffect — eliminates the "select a store" flash.
  const activeStoreId = selectedStoreId || (stores.length === 1 ? (stores[0]?.id ?? '') : '');

  useEffect(() => {
    if (!selectedStoreId && stores.length === 1) setSelectedStoreId(stores[0]!.id);
  }, [stores, selectedStoreId]);

  // Reset conversation when the user switches stores
  useEffect(() => {
    if (!selectedStoreId) return;
    if (prevStoreIdRef.current && prevStoreIdRef.current !== selectedStoreId) {
      abortControllerRef.current?.abort();
      setMessages([]);
      setProgressLogs({});
      setProgressStats({});
      setResultSummaries({});
      setSending(false);
      setStoreChangedNotice(true);
      const t = setTimeout(() => setStoreChangedNotice(false), 5000);
      prevStoreIdRef.current = selectedStoreId;
      return () => clearTimeout(t);
    }
    prevStoreIdRef.current = selectedStoreId;
  }, [selectedStoreId]);

  // Fetch store context for AI prompt enrichment
  const { data: storeContextData } = useQuery({
    queryKey: ['store-context', activeStoreId],
    queryFn: async () => {
      const r = await fetch(`${BASE}/api/stores/${activeStoreId}/context`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return null;
      return r.json();
    },
    enabled: !!activeStoreId && !!token,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, progressLogs]);

  const getHistory = () => messages.map(m => ({
    role: m.role,
    content: m.type === 'text' ? m.content : `[Plan: ${m.summary}]`,
  }));

  const addMessage = (msg: Message) => setMessages(prev => [...prev, msg]);

  const updatePlanState = (id: string, state: PlanMessage['state'], newParams?: any) => {
    setMessages(prev => prev.map(m =>
      m.id === id && m.type === 'plan'
        ? { ...m, state, ...(newParams !== undefined ? { params: newParams } : {}) }
        : m
    ));
  };

  const handleSend = async (promptOverride?: string) => {
    const text = (promptOverride ?? input).trim();
    if (!text || !activeStoreId || sending) return;

    setInput('');
    setSending(true);

    const userMsg: TextMessage = { id: uid(), role: 'user', type: 'text', content: text };
    addMessage(userMsg);

    try {
      const selectedStore = stores.find(s => s.id === activeStoreId);
      const r = await fetch(`${BASE}/api/ai-command/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          prompt: text,
          storeId: activeStoreId,
          history: getHistory(),
          storeContext: {
            domain: selectedStore?.storeUrl,
            vendors: storeContextData?.vendors ?? [],
            productTypes: storeContextData?.productTypes ?? [],
            productCount: storeContextData?.productCount,
          },
        }),
      });

      if (!r.ok) {
        const err = await r.json();
        addMessage({ id: uid(), role: 'assistant', type: 'text', content: `⚠️ ${err.error ?? 'Request failed'}` });
        return;
      }

      const data = await r.json();
      if (data.type === 'plan') {
        addMessage({ id: uid(), role: 'assistant', type: 'plan', storeId: activeStoreId, tool: data.tool, params: data.params, summary: data.summary, state: 'pending' });
      } else {
        addMessage({ id: uid(), role: 'assistant', type: 'text', content: data.content });
      }
    } catch (err: any) {
      addMessage({ id: uid(), role: 'assistant', type: 'text', content: `⚠️ ${err.message ?? 'Something went wrong. Please try again.'}` });
    } finally {
      setSending(false);
    }
  };

  const handleConfirm = useCallback(async (msgId: string, tool: string, params: any, planStoreId: string, overrideParams?: any) => {
    const effectiveParams = overrideParams ?? params;
    // Persist effective params back into the message so Re-run always reproduces
    // what was actually executed (important for sort_collection_oos selectedCollectionIds)
    updatePlanState(msgId, 'running', effectiveParams);
    setProgressLogs(prev => ({ ...prev, [msgId]: [] }));

    const ctrl = new AbortController();
    abortControllerRef.current = ctrl;

    try {
      const r = await fetch(`${BASE}/api/ai-command/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ storeId: selectedStoreId, planStoreId, tool, params: effectiveParams }),
        signal: ctrl.signal,
      });

      if (!r.ok || !r.body) {
        updatePlanState(msgId, 'error');
        setResultSummaries(prev => ({ ...prev, [msgId]: 'Failed to start execution.' }));
        return;
      }

      const reader = r.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finalSummarySet = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data: ProgressLog = JSON.parse(line.slice(6));

            if (data.type === 'product' || data.type === 'collection_result' || data.type === 'status') {
              setProgressLogs(prev => ({ ...prev, [msgId]: [...(prev[msgId] ?? []), data] }));
            }

            if (data.type === 'progress') {
              setProgressStats(prev => ({
                ...prev,
                [msgId]: {
                  processed: data.processed ?? 0,
                  total: data.total ?? 0,
                  updated: data.totalUpdated ?? 0,
                  errors: data.totalErrors ?? 0,
                },
              }));
            }

            if (data.type === 'summary') {
              setResultSummaries(prev => ({ ...prev, [msgId]: data.message ?? 'Complete.' }));
              updatePlanState(msgId, 'done');
              finalSummarySet = true;
            }

            if (data.type === 'error') {
              setResultSummaries(prev => ({ ...prev, [msgId]: data.message ?? 'An error occurred.' }));
              updatePlanState(msgId, 'error');
              finalSummarySet = true;
            }
          } catch { /* skip malformed */ }
        }
      }

      if (!finalSummarySet) updatePlanState(msgId, 'done');
    } catch (err: any) {
      if (err.name === 'AbortError') {
        updatePlanState(msgId, 'cancelled');
      } else {
        updatePlanState(msgId, 'error');
        setResultSummaries(prev => ({ ...prev, [msgId]: err.message ?? 'Execution failed.' }));
      }
    }
  }, [activeStoreId, token]);

  const handleCancel = (msgId: string) => {
    abortControllerRef.current?.abort();
    updatePlanState(msgId, 'cancelled');
  };

  const handleRerun = useCallback((tool: string, params: any, storeId: string, overrideParams?: any) => {
    const newId = uid();
    const effectiveParams = overrideParams ?? params;
    const newMsg: PlanMessage = {
      id: newId,
      role: 'assistant',
      type: 'plan',
      storeId,
      tool,
      params: effectiveParams,
      summary: overrideParams
        ? 'Re-running with edited parameters.'
        : 'Re-running the same plan.',
      state: 'pending',
    };
    addMessage(newMsg);
    // Execute immediately — no second confirmation click required
    handleConfirm(newId, tool, effectiveParams, storeId);
  }, [handleConfirm]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const isEmpty = messages.length === 0;

  return (
    <Layout>
      <div className="flex flex-col" style={{ height: 'calc(100vh - 96px)' }}>
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-5 flex-shrink-0">
          <div>
            <h1 className="text-2xl font-display font-bold flex items-center gap-3">
              <span className="w-8 h-8 rounded-xl bg-primary/20 border border-primary/30 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-primary" />
              </span>
              SoraFlo AI Agent
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Type a plain-English command and the AI will interpret and execute it.
            </p>
          </div>
          <ShopifyTargetStoreSelector
            stores={stores}
            value={selectedStoreId}
            onChange={setSelectedStoreId}
            label="Target Shopify store"
            className="w-full sm:w-72"
          />
        </div>

        {/* Store-changed notice */}
        {storeChangedNotice && (
          <div className="flex-shrink-0 flex items-center gap-2 mb-3 px-4 py-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs text-amber-300">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
            <span>Store changed — conversation reset. Starting fresh for the new store.</span>
            <button
              onClick={() => setStoreChangedNotice(false)}
              className="ml-auto text-amber-400/60 hover:text-amber-300 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Chat area */}
        <div className="flex-1 flex flex-col min-h-0 rounded-2xl border border-border/60 bg-muted/20 overflow-hidden">
          {/* Message thread */}
          <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-5">
            {isEmpty && (
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-4">
                  <Sparkles className="w-8 h-8 text-primary" />
                </div>
                <h2 className="text-lg font-semibold mb-2">What can I help you automate?</h2>
                <p className="text-sm text-muted-foreground max-w-sm mb-8">
                  Tell me what you want to do in plain English. I'll show you a preview before making any changes.
                </p>
                {activeStoreId ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-lg w-full">
                    {SUGGESTED_PROMPTS.map((prompt, i) => (
                      <button
                        key={i}
                        onClick={() => handleSend(prompt)}
                        className="text-left px-4 py-3 rounded-xl border border-border/60 bg-muted/30 hover:bg-muted/50 hover:border-primary/30 text-xs text-muted-foreground hover:text-foreground transition-all"
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                ) : stores.length > 1 ? (
                  <p className="text-sm text-muted-foreground/70">Select a store above to get started.</p>
                ) : null}
              </div>
            )}

            {messages.map(msg => (
              <div key={msg.id} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                {/* Avatar */}
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  msg.role === 'user'
                    ? 'bg-secondary border border-border'
                    : 'bg-primary/20 border border-primary/30'
                }`}>
                  {msg.role === 'user'
                    ? <User className="w-3.5 h-3.5 text-foreground" />
                    : <Bot className="w-3.5 h-3.5 text-primary" />}
                </div>

                {/* Content */}
                <div className={`max-w-[85%] ${msg.role === 'user' ? 'items-end' : 'items-start'} flex flex-col`}>
                  {msg.type === 'text' && (
                    <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-primary/15 border border-primary/20 text-slate-100 rounded-tr-sm'
                        : 'bg-muted/50 border border-border/60 text-foreground rounded-tl-sm'
                    }`}>
                      {msg.content}
                    </div>
                  )}
                  {msg.type === 'plan' && (
                    <div className="w-full min-w-[360px] max-w-[540px]">
                      <PlanCard
                        msg={msg}
                        storeId={selectedStoreId}
                        token={token ?? ''}
                        onConfirm={(overrideParams) => handleConfirm(msg.id, msg.tool, msg.params, msg.storeId, overrideParams)}
                        onCancel={() => handleCancel(msg.id)}
                        onRerun={(overrideParams) => handleRerun(msg.tool, msg.params, msg.storeId, overrideParams)}
                        progressLogs={progressLogs[msg.id] ?? []}
                        progressStats={progressStats[msg.id] ?? null}
                        resultSummary={resultSummaries[msg.id] ?? null}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {sending && (
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-3.5 h-3.5 text-primary" />
                </div>
                <div className="px-4 py-3 rounded-2xl rounded-tl-sm bg-muted/50 border border-border/60">
                  <div className="flex gap-1 items-center">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-500 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-500 animate-bounce" style={{ animationDelay: '120ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-500 animate-bounce" style={{ animationDelay: '240ms' }} />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input bar */}
          <div className="flex-shrink-0 border-t border-border p-4">
            {!activeStoreId && stores.length > 1 && (
              <p className="text-xs text-amber-400/80 mb-2 text-center">Select a store to send commands.</p>
            )}
            <div className="flex gap-3 items-end">
              <textarea
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={activeStoreId
                  ? "Type a command, e.g. 'Add the tag sale to all Toyota products'…"
                  : "Select a store first…"}
                disabled={!activeStoreId || sending}
                rows={1}
                className="flex-1 resize-none bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 transition-colors disabled:opacity-40 leading-relaxed"
                style={{ maxHeight: '120px', overflowY: 'auto' }}
                onInput={e => {
                  const el = e.currentTarget;
                  el.style.height = 'auto';
                  el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
                }}
              />
              <button
                onClick={() => handleSend()}
                disabled={!input.trim() || !selectedStoreId || sending}
                className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center hover:bg-primary/80 disabled:opacity-30 disabled:cursor-not-allowed transition-all flex-shrink-0"
              >
                {sending
                  ? <Loader2 className="w-4 h-4 text-black animate-spin" />
                  : <Send className="w-4 h-4 text-black" />}
              </button>
            </div>
            <p className="text-[10px] text-slate-600 mt-2 text-center">
              Enter to send · Shift+Enter for new line · Review the plan before confirming
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
